import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Phone, Building2, Trash2, MailOpen, Inbox } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";
import api, { apiError } from "../lib/api";
import { formatDateTime } from "../lib/utils-format";

export default function ContactRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removeItem, setRemoveItem] = useState(null);

  const load = () => api.get("/contact-submissions")
    .then((r) => setItems(r.data.items || []))
    .catch((e) => toast.error(apiError(e)))
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const toggleRead = async (it) => {
    try {
      await api.patch(`/contact-submissions/${it.id}`, { read: !it.read });
      setItems((prev) => prev.map((x) => (x.id === it.id ? { ...x, read: !x.read } : x)));
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async () => {
    const it = removeItem;
    setRemoveItem(null);
    try {
      await api.delete(`/contact-submissions/${it.id}`);
      setItems((prev) => prev.filter((x) => x.id !== it.id));
      toast.success("Request deleted");
    } catch (e) { toast.error(apiError(e)); }
  };

  const unread = items.filter((i) => !i.read).length;

  return (
    <div className="p-6 lg:p-8" data-testid="contact-requests-page">
      <PageHeader title="Contact Requests" subtitle="Messages submitted through the website contact form" />

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-14 text-center" data-testid="contact-requests-empty">
          <Inbox className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">No contact requests yet.</p>
        </div>
      ) : (
        <>
          <div className="text-xs text-muted-foreground mb-4" data-testid="contact-requests-count">
            {items.length} request{items.length !== 1 ? "s" : ""}{unread > 0 ? ` · ${unread} unread` : ""}
          </div>
          <div className="space-y-3">
            {items.map((it) => (
              <div
                key={it.id}
                data-testid={`contact-request-${it.id}`}
                className={`rounded-lg border bg-card p-5 ${it.read ? "border-border" : "border-primary/40 bg-primary/[0.03]"}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {!it.read && <span className="h-2 w-2 rounded-full bg-primary shrink-0" data-testid={`unread-dot-${it.id}`} />}
                      <span className="font-semibold text-foreground">{it.name}</span>
                      {it.company && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Building2 className="h-3 w-3" />{it.company}</span>}
                      {it.partnership_type && <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary" data-testid={`contact-request-type-${it.id}`}>{it.partnership_type}</span>}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground flex-wrap">
                      <a href={`mailto:${it.email}`} className="inline-flex items-center gap-1 hover:text-foreground"><Mail className="h-3 w-3" />{it.email}</a>
                      {it.phone && <a href={`tel:${it.phone}`} className="inline-flex items-center gap-1 hover:text-foreground"><Phone className="h-3 w-3" />{it.phone}</a>}
                      <span>{formatDateTime(it.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => toggleRead(it)} title={it.read ? "Mark unread" : "Mark read"} data-testid={`toggle-read-${it.id}`}>
                      <MailOpen className={`h-4 w-4 ${it.read ? "text-muted-foreground" : "text-primary"}`} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setRemoveItem(it)} title="Delete" data-testid={`delete-${it.id}`}>
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </Button>
                  </div>
                </div>
                <p className="mt-3 text-sm text-foreground whitespace-pre-wrap leading-relaxed">{it.message}</p>
              </div>
            ))}
          </div>
        </>
      )}

      <AlertDialog open={!!removeItem} onOpenChange={(o) => { if (!o) setRemoveItem(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete contact request?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove the message from {removeItem?.name}.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
